// PMS/src/components/InventoryDashboard.js
import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../App'; // Import AuthContext from App.js
import { db } from '../firebaseConfig'; // Import db instance
import {
    collection,
    doc,
    getDoc,
    addDoc,
    updateDoc,
    deleteDoc,
    query,
    onSnapshot
} from 'firebase/firestore';

// --- 0. Basic Styling (Copied from App.js for now) ---
// In a real app, you'd centralize these or use a CSS file
const formStyles = {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    marginBottom: '20px',
    padding: '15px',
    border: '1px solid #eee',
    borderRadius: '5px',
    backgroundColor: '#f9f9f9',
};

const inputStyles = {
    padding: '10px',
    borderRadius: '4px',
    border: '1px solid #ccc',
};

const buttonStyles = {
    padding: '10px 15px',
    borderRadius: '4px',
    border: 'none',
    cursor: 'pointer',
    backgroundColor: '#007bff',
    color: 'white',
    fontSize: '1em',
};

const dangerButtonStyles = {
    ...buttonStyles,
    backgroundColor: '#dc3545',
};

const successButtonStyles = {
    ...buttonStyles,
    backgroundColor: '#28a745',
};

const messageStyles = {
    padding: '10px',
    borderRadius: '4px',
    margin: '10px 0',
};

const successMessageStyles = {
    ...messageStyles,
    backgroundColor: '#d4edda',
    color: '#155724',
};

const errorMessageStyles = {
    ...messageStyles,
    backgroundColor: '#f8d7da',
    color: '#721c24',
};

const itemCardStyles = {
    border: '1px solid #ddd',
    padding: '15px',
    marginBottom: '10px',
    borderRadius: '5px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap', // Allow items to wrap on smaller screens
};

const itemDetailsStyles = {
    flexGrow: 1,
    minWidth: '200px', // Ensure details don't get too small
    marginBottom: '10px', // Space between details and buttons on wrap
};

const itemActionsStyles = {
    display: 'flex',
    gap: '10px',
};


const itemEditFormStyles = {
    ...formStyles,
    marginTop: '10px',
    backgroundColor: '#e0f7fa',
    width: '100%', // Take full width within the card
};


// --- Dashboard Component (The inventory management part) ---
export default function InventoryDashboard() { // Renamed from Dashboard and made default export
    const { currentUser, logout } = useContext(AuthContext);
    const [personnelData, setPersonnelData] = useState(null);
    const [loadingPersonnel, setLoadingPersonnel] = useState(true);
    const [itemForm, setItemForm] = useState({ name: '', quantity: 0, unitOfQuantity: '', minimumQty: 0, brand: '', location: "", category: '' });
    const [items, setItems] = useState([]);
    const [message, setMessage] = useState('');
    const [isSuccess, setIsSuccess] = useState(false);
    const [editingItem, setEditingItem] = useState(null);

    // Fetch Personnel Data (Real-time with onSnapshot, as we previously discussed)
    useEffect(() => {
        let unsubscribePersonnel = () => {};

        if (currentUser) {
            setLoadingPersonnel(true);
            const docRef = doc(db, 'personnel', currentUser.uid);

            unsubscribePersonnel = onSnapshot(docRef, (docSnap) => {
                if (docSnap.exists()) {
                    setPersonnelData(docSnap.data());
                    setLoadingPersonnel(false);
                } else {
                    setPersonnelData(null);
                    setLoadingPersonnel(false);
                }
            }, (error) => {
                console.error("Error fetching real-time personnel data:", error);
                setPersonnelData({ message: `Error: ${error.message}` });
                setLoadingPersonnel(false);
            });
        } else {
            setPersonnelData(null);
            setLoadingPersonnel(false);
        }
        return () => unsubscribePersonnel();
    }, [currentUser]);

    // CRUD: Read Items (Real-time with onSnapshot)
    useEffect(() => {
        if (!currentUser) {
            setItems([]);
            return;
        }

        const itemsCollectionRef = collection(db, 'items');
        const q = query(itemsCollectionRef);

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const itemsData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setItems(itemsData);
        }, (error) => {
            console.error("Error fetching real-time items:", error);
            showMessage(`Error fetching items: ${error.message}`, false); // Use local showMessage
        });

        return unsubscribe;
    }, [currentUser]);

    const handleFormChange = (e) => {
        const { name, value } = e.target;
        setItemForm(prev => ({ ...prev, [name]: name === 'quantity' || name === 'minimumQty' ? Number(value) : value }));
    };

    const showMessage = (msg, success) => {
        setMessage(msg);
        setIsSuccess(success);
        setTimeout(() => setMessage(''), 3000);
    };

    // CRUD: Create Item
    const handleAddItem = async (e) => {
        e.preventDefault();
        if (!itemForm.name || !itemForm.category || itemForm.quantity < 0  || !itemForm.unitOfQuantity|| !itemForm.location) {
            showMessage("Please fill all item fields correctly (quantity >= 0).", false);
            return;
        }
        try {
            await addDoc(collection(db, 'items'), itemForm);
            showMessage("Item added successfully!", true);
            setItemForm({ name: '', quantity: 0, unitOfQuantity: '', minimumQty: 0, brand: '', location: "", category: '' });
        } catch (error) {
            console.error("Error adding item:", error);
            showMessage(`Error adding item: ${error.message}`, false);
        }
    };

    // CRUD: Update Item
    const handleUpdateItem = async (itemId) => {
        if (!editingItem.name || !editingItem.category || editingItem.quantity < 0) {
            showMessage("Please fill all item fields correctly for update (quantity >= 0).", false);
            return;
        }
        try {
            const itemRef = doc(db, 'items', itemId);
            await updateDoc(itemRef, editingItem);
            showMessage("Item updated successfully!", true);
            setEditingItem(null);
        } catch (error) {
            console.error("Error updating item:", error);
            showMessage(`Error updating item: ${error.message}`, false);
        }
    };

    // CRUD: Delete Item
    const handleDeleteItem = async (itemId) => {
        if (window.confirm("Are you sure you want to delete this item?")) {
            try {
                const itemRef = doc(db, 'items', itemId);
                await deleteDoc(itemRef);
                showMessage("Item deleted successfully!", true);
            } catch (error) {
                console.error("Error deleting item:", error);
                showMessage(`Error deleting item: ${error.message}`, false);
            }
        }
    };

    return (
        <div>
            <h1>Property Management</h1> {/* Renamed from "Inventory Dashboard" for clarity */}
            <div style={{ marginBottom: '20px', paddingBottom: '20px', borderBottom: '1px solid #eee' }}>
                <p>Welcome, <strong>{currentUser.email}</strong>!</p>
                <p>Auth UID: <code>{currentUser.uid}</code></p>
                <h3>Your Personnel Profile:</h3>
                {loadingPersonnel ? (
                    <p>Loading personnel data...</p>
                ) : (
                    <pre style={{ backgroundColor: '#f0f0f0', padding: '10px', borderRadius: '4px' }}>
                        {personnelData ? JSON.stringify(personnelData, null, 2) : "No personnel data found."}
                    </pre>
                )}
                <button onClick={logout} style={dangerButtonStyles}>Log Out</button>
            </div>

            {message && (
                <div style={isSuccess ? successMessageStyles : errorMessageStyles}>
                    {message}
                </div>
            )}

            {/* --- Add New Item Form --- */}
            <h2>Add New Item</h2>
            <form onSubmit={handleAddItem} style={formStyles}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <label htmlFor="itemName" style={{ minWidth: '80px' }}>Item Name:</label>
                    <input
                        type="text"
                        id="itemName"
                        name="name"
                        placeholder="e.g., Laptop, Keyboard"
                        value={itemForm.name}
                        onChange={handleFormChange}
                        style={inputStyles}
                        required
                    />
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <label htmlFor="itemQuantity" style={{ minWidth: '80px' }}>Quantity:</label>
                    <input
                        type="number"
                        id="itemQuantity"
                        name="quantity"
                        placeholder="50"
                        value={itemForm.quantity} 
                        onChange={handleFormChange}
                        style={inputStyles}
                        min="0"
                        required
                    />
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <label htmlFor="itemUnit" style={{ minWidth: '80px' }}>Unit:</label>
                    <input
                        type="text"
                        id="itemUnit"
                        name="unitOfQuantity"
                        placeholder="e.g., pcs, boxes, Kg"
                        value={itemForm.unitOfQuantity}
                        onChange={handleFormChange}
                        style={inputStyles}
                        required
                    />
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <label htmlFor="itemCategory" style={{ minWidth: '80px' }}>Category:</label>
                    <input
                        type="text"
                        id="itemCategory"
                        name="category"
                        placeholder="e.g., Electronics, Stationery"
                        value={itemForm.category}
                        onChange={handleFormChange}
                        style={inputStyles}
                        required
                    />
                </div>
                   <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <label htmlFor="itemLocation" style={{ minWidth: '80px' }}>Location:</label>
                    <input
                        type="text"
                        id="itemLocation"
                        name="location"
                        placeholder="e.g., Warehouse A, Shelf 3"
                        value={itemForm.location}
                        onChange={handleFormChange}
                        style={inputStyles}
                        required
                    />
                </div>
                <button type="submit" style={successButtonStyles}>Add Item</button>
            </form>

            {/* --- Items List (Read) --- */}
            <h2>Current Inventory Items</h2>
            {items.length === 0 ? (
                <p>No items found. Add some above!</p>
            ) : (
                <div>
                    {items.map(item => (
                         <div key={item.id} style={itemCardStyles}>
                            <div style={itemDetailsStyles}>
                                <strong>{item.name}</strong> ({item.category}) - Quantity: {item.quantity} {item.unitOfQuantity} in {item.location}
                                {item.brand && ` (Brand: ${item.brand})`}
                            </div>
                            <div style={itemActionsStyles}>
                                <button
                                    onClick={() => setEditingItem(editingItem && editingItem.id === item.id ? null : { ...item })}
                                    style={{ ...buttonStyles, marginRight: '10px' }}
                                >
                                    {editingItem && editingItem.id === item.id ? 'Cancel' : 'Edit'}
                                </button>
                                <button onClick={() => handleDeleteItem(item.id)} style={dangerButtonStyles}>
                                    Delete
                                </button>
                            </div>
                            {editingItem && editingItem.id === item.id && (
                                <div style={itemEditFormStyles}>
                                    <h3>Edit Item: {item.name}</h3>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                                        <label htmlFor={`editName-${item.id}`} style={{ minWidth: '80px' }}>Name:</label>
                                        <input
                                            type="text"
                                            id={`editName-${item.id}`}
                                            name="name"
                                            value={editingItem.name}
                                            onChange={(e) => setEditingItem(prev => ({ ...prev, name: e.target.value }))}
                                            style={inputStyles}
                                            required
                                        />
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                                        <label htmlFor={`editQuantity-${item.id}`} style={{ minWidth: '80px' }}>Quantity:</label>
                                        <input
                                            type="number"
                                            id={`editQuantity-${item.id}`}
                                            name="quantity"
                                            value={editingItem.quantity}
                                            onChange={(e) => setEditingItem(prev => ({ ...prev, quantity: Number(e.target.value) }))}
                                            style={inputStyles}
                                            min="0"
                                            required
                                        />
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                                        <label htmlFor={`editUnit-${item.id}`} style={{ minWidth: '80px' }}>Unit:</label>
                                        <input
                                            type="text"
                                            id={`editUnit-${item.id}`}
                                            name="unitOfQuantity"
                                            value={editingItem.unitOfQuantity}
                                            onChange={(e) => setEditingItem(prev => ({ ...prev, unitOfQuantity: e.target.value }))}
                                            style={inputStyles}
                                            required
                                        />
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                                        <label htmlFor={`editCategory-${item.id}`} style={{ minWidth: '80px' }}>Category:</label>
                                        <input
                                            type="text"
                                            id={`editCategory-${item.id}`}
                                            name="category"
                                            value={editingItem.category}
                                            onChange={(e) => setEditingItem(prev => ({ ...prev, category: e.target.value }))}
                                            style={inputStyles}
                                            required
                                        />
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                                        <label htmlFor={`editLocation-${item.id}`} style={{ minWidth: '80px' }}>Location:</label>
                                        <input
                                            type="text"
                                            id={`editLocation-${item.id}`}
                                            name="location"
                                            value={editingItem.location}
                                            onChange={(e) => setEditingItem(prev => ({ ...prev, location: e.target.value }))}
                                            style={inputStyles}
                                            required
                                        />
                                    </div>
                                    <button onClick={() => handleUpdateItem(item.id)} style={successButtonStyles}>
                                        Save Changes
                                    </button>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
